import {
  Profile,
  User,
  UserManager,
  WebStorageStateStore,
  UserSettings
} from 'oidc-client'
import {
  ApplicationPaths,
  ApplicationName
} from './api-authorization.constants'

export type IAuthenticationResult =
  | SuccessAuthenticationResult
  | FailureAuthenticationResult
  | RedirectAuthenticationResult

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success
  state: any
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail
  message: string
}

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect
}

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail
}

export interface IUser {
  name: string
}

export class AuthorizeService {
  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.

  private popUpDisabled = true
  private userManager!: UserManager

  public isAuthenticated(): Promise<boolean> {
    return this.getUserFromStorage().then((u) => !!u)
  }

  public async getAccessToken(): Promise<string | null> {
    await this.ensureUserManagerInitialized()
    return this.userManager.getUser().then((user) => user && user.access_token)
  }
  public async getIdToken(): Promise<string | null> {
    await this.ensureUserManagerInitialized()
    return this.userManager.getUser().then((user) => user && user.id_token)
  }
  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  public async signIn(state: any): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized()
    let user: User
    try {
      user = await this.userManager.signinSilent(this.createArguments())
      return this.success(state)
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log('Silent authentication error: ', silentError)

      try {
        if (this.popUpDisabled) {
          throw new Error(
            "Popup disabled. Change 'authorize.service.ts:AuthorizeService.popupDisabled' to false to enable it."
          )
        }
        user = await this.userManager.signinPopup(this.createArguments())
        return this.success(state)
      } catch (popupError) {
        if (popupError === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('The user closed the window.')
        } else if (!this.popUpDisabled) {
          console.log('Popup authentication error: ', popupError)
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager.signinRedirect(this.createArguments(state))
          return this.redirect()
        } catch (redirectError) {
          console.log('Redirect authentication error: ', redirectError)
          return this.error(redirectError as string)
        }
      }
    }
  }

  public async completeSignIn(url: string): Promise<IAuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized()
      const user = await this.userManager.signinCallback(url)
      return this.success(user && user.state)
    } catch (error) {
      console.log('There was an error signing in: ', error)
      return this.error('There was an error signing in.')
    }
  }

  public async signOut(state: any): Promise<IAuthenticationResult> {
    try {
      if (this.popUpDisabled) {
        throw new Error(
          "Popup disabled. Change 'authorize.service.ts:AuthorizeService.popupDisabled' to false to enable it."
        )
      }

      await this.ensureUserManagerInitialized()
      await this.userManager.signoutPopup(this.createArguments())
      return this.success(state)
    } catch (popupSignOutError) {
      console.log('Popup signout error: ', popupSignOutError)
      try {
        await this.userManager.signoutRedirect(this.createArguments(state))
        return this.redirect()
      } catch (redirectSignOutError) {
        console.log('Redirect signout error: ', popupSignOutError)
        return this.error(redirectSignOutError as string)
      }
    }
  }

  public async completeSignOut(url: string): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized()
    try {
      const state = await this.userManager.signoutCallback(url)
      return this.success(state && state.name)
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`)
      return this.error(error as string)
    }
  }

  private createArguments(state?: any): any {
    return { useReplaceToNavigate: true, data: state }
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message }
  }

  private success(state: any): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state }
  }

  private redirect(): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  private async ensureUserManagerInitialized(): Promise<any> {
    if (this.userManager !== undefined) {
      return
    }
    // const response = await fetch(
    //   ApplicationPaths.ApiAuthorizationClientConfigurationUrl
    // )
    // if (!response.ok) {
    //   throw new Error(`Could not load settings for '${ApplicationName}'`)
    // }

    const authority = process.env.OIDC_AUTHORITY
    const client_id = process.env.OIDC_CLIENT_ID
    const response_type = 'id_token token'
    const scope = 'openid profile groups'

    const settings: any = {
      authority: authority,
      client_id: client_id,
      redirect_uri: window.location.origin + '/authentication',
      response_type: response_type,
      scope: scope,
      post_logout_redirect_uri: window.location.origin + '/unauthorized',
      silent_redirect_uri: window.location.origin + '/silent-renew.html',
      revokeAccessTokenOnSignout: false,
      accessTokenExpiringNotificationTime: 10,
      automaticSilentRenew: true,
      filterProtocolClaims: true,
      loadUserInfo: true
      // metadata: {
      //   end_session_endpoint: window.location.origin + "unauthorized" // Or however you want to clear sessionStorage or revoke the token
      // }
    }

    this.userManager = new UserManager(settings)
    this.userManager.events.addUserSignedOut(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async () => await this.userManager.removeUser()
    )
  }
  public async getUser(): Promise<IUser | Profile | null> {
    return await this.getUserFromStorage()
  }
  private getUserFromStorage(): Promise<IUser | Profile | null> {
    return this.ensureUserManagerInitialized().then(() =>
      this.userManager.getUser().then((u) => u && u.profile)
    )
  }
}
